import React, { useState, useEffect, useCallback } from 'react';
import { Box, IconButton, Typography, TextField, Button, Badge, Tooltip } from '@mui/material';
import { Edit, Check, Clear, ExpandLess, Share, PeopleAlt, GroupAdd, ArrowDropDownCircleOutlined } from '@mui/icons-material';
import { usePlan } from 'src/hooks/PlanProvider';
import SharePlanDialog from '../PlanBuilder/SharePlanDialog';
import { useQuery } from '@tanstack/react-query';
import { APIClient } from 'src/lib/api_client';

import './PlanStepperHeader.scss';
import { isDraftPlan, isSavedPlan, SavedPlan, DraftPlan, Plan } from 'src/types/interfaces';

interface PlanStepperHeaderProps {
  collapseHandler?: () => void;
}

const PlanStepperHeader: React.FC<PlanStepperHeaderProps> = ({ collapseHandler }) => {
  const [isEditing, setIsEditing] = useState(false);
  const { selectedPlan, setSelectedPlan, savePlan } = usePlan();
  const [localPlanName, setLocalPlanName] = useState(selectedPlan.name);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const planIsShareable = isSavedPlan(selectedPlan) && selectedPlan.slug;
  const currentPlanId = planIsShareable ? (selectedPlan as SavedPlan).id : null;

  const {
    data: planDetails,
  } = useQuery<any>({
    queryKey: ['planDetails', currentPlanId],
    queryFn: async () => {
      if (!currentPlanId) throw new Error('No plan ID to fetch');
      const response = await APIClient.get(`/plans/${currentPlanId}/`);
      return response.data?.plan ?? response.data;
    },
    enabled: !!currentPlanId,
    refetchInterval: 30000,
    refetchOnWindowFocus: true,
  });

  const handleEditClick = (): void => {
    setLocalPlanName(selectedPlan.name);
    setIsEditing(true);
  };

  const handleCancelClick = () => {
    setLocalPlanName(selectedPlan.name);
    setIsEditing(false);
  };

  const handleSaveClick = () => {
    if (localPlanName.trim() !== selectedPlan.name) {
      const updatedPlan = { ...selectedPlan, name: localPlanName.trim() } as SavedPlan;
      savePlan(updatedPlan);
    }
    setIsEditing(false);
  };

  const handleShareClick = () => {
    setShareDialogOpen(true);
  };

  const displayRsvpCount = planDetails?.rsvpsCount ?? ((isSavedPlan(selectedPlan) && (selectedPlan as SavedPlan).rsvpsCount) || 0);

  return (
    <Box className="plan-stepper-header" display="flex" alignItems="center" justifyContent="space-between">
      <Box display="flex" alignItems="center" flexGrow={1}>
        {isEditing ? (
          <>
            <IconButton size="medium" onClick={handleCancelClick} data-testid="plan-stepper-name-clear">
              <Clear className="mini-button-icon" />
            </IconButton>
            <TextField
              color="secondary"
              size="small"
              variant="outlined"
              value={localPlanName}
              inputProps={{ "data-testid": 'plan-stepper-name-input' }}
              onChange={(e) => setLocalPlanName(e.target.value)}
              sx={{ mr: 1, flexGrow: 1 }}
            />
            <IconButton size="medium" onClick={handleSaveClick} data-testid="plan-stepper-name-save">
              <Check className="mini-button-icon-primary" />
            </IconButton>
          </>
        ) : (
          <>
            <IconButton size="medium" onClick={handleEditClick} data-testid="plan-stepper-name-edit" sx={{ mr: 0.5 }}>
              <Edit className="mini-button-icon" />
            </IconButton>
            <Typography variant="h6" className="title" data-testid="plan-stepper-name">
              {selectedPlan.name}{isDraftPlan(selectedPlan) && ' (Draft)'}
            </Typography>
            {planIsShareable && displayRsvpCount > 0 && (
              <Badge badgeContent={displayRsvpCount} color="primary" sx={{ ml: 1.5, mr: 0.5 }} data-testid="guest-count-badge">
                <PeopleAlt fontSize="small" />
              </Badge>
            )}
          </>
        )}
      </Box>

      <Box display="flex" alignItems="center" className="button-group">
        {planIsShareable && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<Share />}
            onClick={handleShareClick}
            sx={{ mr: collapseHandler ? 1 : 0, textTransform: 'none' }}
            data-testid="share-plan-button"
          >
            Share
          </Button>
        )}
        {collapseHandler && (
          <IconButton size="medium" className="button" onClick={collapseHandler} data-testid="collapse-button">
            <ExpandLess />
          </IconButton>
        )}
      </Box>
      {planIsShareable && (
        <SharePlanDialog open={shareDialogOpen} onClose={() => setShareDialogOpen(false)} />
      )}
    </Box>
  );
};

export default PlanStepperHeader;
